import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { fmtNumber } from "../lib/utils";
import { Beaker } from "lucide-react";

const GAS_COLORS = {
  CO2: "#064E3B",
  CH4: "#10B981",
  N2O: "#A7F3D0",
  HFCs: "#F59E0B",
  PFCs: "#EF4444",
  SF6: "#A855F7",
  NF3: "#3B82F6",
};

const GAS_LABEL = {
  CO2: "CO₂", CH4: "CH₄", N2O: "N₂O",
  HFCs: "HFCs", PFCs: "PFCs", SF6: "SF₆", NF3: "NF₃",
};

export default function GhgInventoryWidget({ inventory }) {
  if (!inventory) return null;
  const visible = inventory.gases.filter((g) => g.tco2e > 0);
  return (
    <div className="card p-4" data-testid="ghg-inventory-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="section-label flex items-center gap-2">
            <Beaker size={12} /> GHG Inventory — Gas Composition
          </div>
          <div className="text-xs text-ink-soft mt-0.5">
            Decomposition of total Scope 1+2+3 emissions across the 7 Kyoto GHGs · {inventory.period}
          </div>
        </div>
        <div className="text-right">
          <div className="dlabel">Total</div>
          <div className="font-mono text-lg font-semibold" data-testid="ghg-total">
            {fmtNumber(inventory.total_tco2e)} <span className="text-xs text-ink-soft">tCO₂e</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={visible}
                dataKey="tco2e"
                nameKey="gas"
                innerRadius={45}
                outerRadius={80}
                paddingAngle={1}
              >
                {visible.map((g) => (
                  <Cell key={g.gas} fill={GAS_COLORS[g.gas] || "#94A3B8"} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v, _n, p) => [
                  `${fmtNumber(v)} tCO₂e (${p.payload.pct}%)`,
                  GAS_LABEL[p.payload.gas] || p.payload.gas,
                ]}
              />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                formatter={(value) => GAS_LABEL[value] || value}
                wrapperStyle={{ fontSize: 11 }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div>
          <table className="dense w-full">
            <thead>
              <tr><th>Gas</th><th className="text-right">tCO₂e</th><th className="text-right">% of total</th></tr>
            </thead>
            <tbody data-testid="ghg-table">
              {inventory.gases.map((g) => (
                <tr key={g.gas}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="dot" style={{ background: GAS_COLORS[g.gas] || "#94A3B8" }} />
                      <span className="font-medium">{GAS_LABEL[g.gas] || g.gas}</span>
                    </div>
                  </td>
                  <td className="num">{fmtNumber(g.tco2e)}</td>
                  <td className="num text-ink-soft">{g.pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
